import type { FC, ReactNode } from 'react';
import React, { useMemo } from 'react';

import { Box, Typography, useTheme, Zoom } from '@mui/material';

import { MuiButton, MuiLoadingButton, MuiPreloader, MuiTooltip } from '@shared/ui-kit';

import { StyleSimpleBorderContainer } from './SimpleBorderContainer.style';

import type { MuiButtonProps } from '@shared/ui-kit';
import type { MuiLoadingButtonProps } from '@shared/ui-kit/buttons/MuiLoadingButton/MuiLoadingButton';

type SimpleBorderContainerProps = {
  children: ReactNode;
  isShowBackground: boolean;
  isShowBorder: boolean;
  isShowPadding: boolean;
  paddingLeft?: string;
  customPaddingContainer?: string;
  customPaddingHeader?: string | null;
  title?: string;
  variant?: 'base' | 'withHeaderAndFooter' | 'withHeader';
  footerLeftBtnProps?: MuiButtonProps & { isShow: boolean; tooltipText?: string };
  footerRightBtnProps?: MuiLoadingButtonProps & { isShow: boolean; tooltipText?: string };
  isShowFooter?: boolean;
  isShowHeader?: boolean;
  counter?: number;
  isShowLoader?: boolean;
  customRightSide?: React.ReactNode | null;
  onClickHeader?: () => void;
  isShowContainerBackground?: boolean;
  isShowContainerBorder?: boolean;
  isCustomRightSideFullWidth?: boolean;
  onClickContainer?: () => void;
  isBlur?: boolean;
};
export const SimpleBorderContainer: FC<SimpleBorderContainerProps> = ({
  children,
  isShowBorder,
  isShowHeader = true,
  isShowContainerBackground,
  isShowContainerBorder,
  isShowBackground,
  isShowPadding,
  paddingLeft,
  customPaddingHeader,
  customPaddingContainer,
  title,
  variant = 'base',
  footerLeftBtnProps,
  isShowFooter = true,
  footerRightBtnProps,
  customRightSide,
  isCustomRightSideFullWidth,
  counter,
  isShowLoader,
  onClickHeader,
  isBlur,
  onClickContainer,
}) => {
  const theme = useTheme();
  return useMemo(() => {
    switch (variant) {
      case 'base':
        return (
          <StyleSimpleBorderContainer
            isShowBackground={isShowBackground}
            isShowBorder={isShowBorder}
            isShowPadding={isShowPadding}
            paddingLeft={paddingLeft}
            customPaddingContainer={customPaddingContainer}
          >
            {children}
          </StyleSimpleBorderContainer>
        );
      case 'withHeader':
        return (
          <StyleSimpleBorderContainer
            isShowBackground={isShowBackground}
            isShowBorder={isShowBorder}
            isShowPadding={false}
          >
            {isShowHeader && (
              <Box
                sx={{
                  width: '100%',
                  borderBottom: isShowBorder ? `1px solid ${theme.palette.case.neutral.n200}` : '',
                  padding: customPaddingHeader ?? '14px 16px 12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    cursor: onClickHeader ? 'pointer' : 'initial',
                  }}
                  onClick={onClickHeader}
                >
                  <Typography variant="t14m" noWrap color={(theme) => theme.palette.case.neutral.n800}>
                    {title}
                  </Typography>

                  <Zoom in={!!isShowLoader && !customRightSide} unmountOnExit>
                    <Box display={'flex'} alignItems={'center'}>
                      <MuiPreloader size={'small'} isShow />
                      <Typography ml={'6px'} color={theme.palette.case.neutral.n600} variant={'t12m'}>
                        Loading...
                      </Typography>
                    </Box>
                  </Zoom>
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'flex-end'}
                  alignItems={'center'}
                  width={isCustomRightSideFullWidth ? '100%' : 'auto'}
                >
                  {customRightSide && customRightSide}

                  <Zoom in={!!isShowLoader && !customRightSide} unmountOnExit>
                    <Box display={'flex'} alignItems={'center'}>
                      <MuiPreloader size={'small'} isShow />
                      <Typography ml={'6px'} color={theme.palette.case.neutral.n600} variant={'t12m'}>
                        Loading...
                      </Typography>
                    </Box>
                  </Zoom>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                padding: isShowPadding ? customPaddingContainer || '16px' : 0,
                paddingLeft: paddingLeft ? paddingLeft : isShowPadding ? '16px' : 0,
              }}
            >
              {children}
            </Box>
          </StyleSimpleBorderContainer>
        );
      case 'withHeaderAndFooter':
        return (
          <StyleSimpleBorderContainer
            isShowBackground={isShowBackground}
            isShowBorder={isShowBorder}
            isShowPadding={false}
          >
            {isShowHeader && (
              <Box
                sx={{
                  width: '100%',
                  borderBottom: isShowBorder ? `1px solid ${theme.palette.case.neutral.n200}` : '',
                  padding: customPaddingHeader ?? '14px 16px 12px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: onClickHeader ? 'pointer' : 'initial',
                  }}
                  onClick={onClickHeader}
                >
                  <Typography variant={'s3'} noWrap color={theme.palette.case.neutral.n800}>
                    {title}
                  </Typography>
                  <Zoom in={!!counter} unmountOnExit>
                    <Box
                      sx={{
                        ml: '6px',
                        width: '20px',
                        height: '20px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: theme.palette.case.neutral.n200,
                      }}
                    >
                      <Typography color={theme.palette.case.neutral.n900} variant={'t12r'}>
                        {counter}
                      </Typography>
                    </Box>
                  </Zoom>
                </Box>
                <Box
                  display={'flex'}
                  justifyContent={'flex-end'}
                  alignItems={'center'}
                  width={isCustomRightSideFullWidth ? '100%' : 'auto'}
                >
                  {customRightSide && customRightSide}

                  <Zoom in={!!isShowLoader && !customRightSide} unmountOnExit>
                    <Box display={'flex'} alignItems={'center'}>
                      <MuiPreloader size={'small'} isShow />
                      <Typography ml={'6px'} color={theme.palette.case.neutral.n600} variant={'t12m'}>
                        Loading...
                      </Typography>
                    </Box>
                  </Zoom>
                </Box>
              </Box>
            )}

            <Box
              onClick={onClickContainer}
              sx={{
                padding: isShowPadding ? customPaddingContainer || '16px' : 0,
                paddingLeft: paddingLeft ? paddingLeft : isShowPadding ? '16px' : 0,
                backgroundColor: isShowContainerBackground ? theme.palette.case.neutral.n0 : '',
                border: isShowContainerBorder ? `1px solid ${theme.palette.case.neutral.n200}` : '',
                borderRadius: '8px',
                cursor: onClickContainer ? 'pointer' : 'default',
                ...(isBlur && {
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    zIndex: 2,
                    cursor: 'pointer',
                    width: '100%',
                    height: '100%',
                    backdropFilter: 'blur(5px)',
                    background: 'rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                  },
                }),
              }}
            >
              {children}
            </Box>

            {isShowFooter && (
              <Box
                sx={{
                  width: '100%',
                  borderTop: `1px solid ${theme.palette.case.neutral.n200}`,
                  padding: '7px 16px 7px 16px',
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                {footerLeftBtnProps?.isShow ? (
                  <MuiTooltip title={footerLeftBtnProps?.tooltipText || ''}>
                    <Box>
                      <MuiButton
                        variant={'text'}
                        color={'secondary'}
                        size={'small'}
                        {...footerLeftBtnProps}
                      />
                    </Box>
                  </MuiTooltip>
                ) : (
                  <Box />
                )}

                {footerRightBtnProps?.isShow ? (
                  <MuiLoadingButton
                    variant={'text'}
                    color={'secondary'}
                    size={'small'}
                    {...footerRightBtnProps}
                  />
                ) : (
                  <></>
                )}
              </Box>
            )}
          </StyleSimpleBorderContainer>
        );
      default:
        return <></>;
    }
  }, [
    variant,
    isShowBackground,
    isShowBorder,
    isShowPadding,
    paddingLeft,
    customPaddingContainer,
    children,
    isShowHeader,
    theme.palette.case.neutral.n200,
    theme.palette.case.neutral.n600,
    theme.palette.case.neutral.n800,
    theme.palette.case.neutral.n900,
    theme.palette.case.neutral.n0,
    customPaddingHeader,
    onClickHeader,
    title,
    isShowLoader,
    customRightSide,
    isCustomRightSideFullWidth,
    counter,
    onClickContainer,
    isShowContainerBackground,
    isShowContainerBorder,
    isBlur,
    isShowFooter,
    footerLeftBtnProps,
    footerRightBtnProps,
  ]);
};
